/**
 * The rules behind the government-identity step, as pure functions.
 *
 * After Microsoft sign-in the portal still has to establish that the person is reachable
 * at an official agency address: they enter that address, and confirm a one-time code sent
 * to it. The two screens that ask for those are ported from the le-portal-ui-v3 prototype
 * (`agency-email` and `verification` in src/LoginFlow.tsx).
 *
 * What is *not* here is as deliberate as what is. The code, how long it is, how long it
 * lives and when another may be asked for are all the server's (api/verificationApi.ts):
 * the client is told the length and the two deadlines per challenge and renders them. A
 * client that held the code, or disagreed with the server about its shape, could be made
 * to accept a code the server would not (PORT-DEFENSE). What remains here is the screen's
 * own arithmetic — address shape, masking, digit filtering, and counting down to an
 * instant.
 */
import { z } from 'zod';
import { EMAIL_PATTERN } from '@/utils/formSchemas';

/** Trims and lowercases an address so two spellings of one mailbox compare equal. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Whether an address is well-formed enough to send a code to.
 *
 * Shape only. Whether the domain actually belongs to a law-enforcement agency is a
 * server-side decision, and is deliberately not guessed at here.
 */
export const agencyEmailSchema = z.string().trim().toLowerCase().regex(EMAIL_PATTERN);

export function isValidAgencyEmail(email: string): boolean {
  return agencyEmailSchema.safeParse(email).success;
}

/** Whether the address and its confirmation are both valid and the same mailbox. */
export function agencyEmailsMatch(email: string, confirmation: string): boolean {
  const normalized = normalizeEmail(email);
  const normalizedConfirmation = normalizeEmail(confirmation);
  return (
    isValidAgencyEmail(normalized) &&
    normalizedConfirmation.length > 0 &&
    normalized === normalizedConfirmation
  );
}

/**
 * The address as the code screen shows it back — enough to recognize, not enough to read
 * off a shoulder-surfed screen.
 */
export function maskEmail(email: string): string {
  const trimmed = email.trim();
  return trimmed.replace(
    /^(.{1,2}).*(@.*)$/,
    (_match, start: string, domain: string) => `${start}••••${domain}`,
  );
}

/**
 * Drops everything that is not a digit and caps the result at the length the server said
 * its code has.
 *
 * The cap is a parameter rather than a constant because the length belongs to the
 * challenge: the screen is told it when the code is sent, so a server-side policy change
 * needs no client release.
 */
export function sanitizeCode(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, Math.max(0, maxLength));
}

/**
 * Whole seconds left until an instant, never negative.
 *
 * The server sends absolute instants rather than durations, so a countdown that was
 * backgrounded, suspended or resumed recomputes against the clock instead of drifting by
 * however long the tab was asleep. A missing or unparseable instant counts as elapsed —
 * the server refuses an early resend anyway, so the honest failure is the one that comes
 * back from asking, not a countdown stuck forever.
 */
export function secondsUntil(instant: string | undefined, now: number = Date.now()): number {
  if (instant === undefined) {
    return 0;
  }

  const target = Date.parse(instant);
  if (Number.isNaN(target)) {
    return 0;
  }

  return Math.max(0, Math.ceil((target - now) / 1000));
}

/**
 * How far the browser's clock is from the server's, in milliseconds.
 *
 * Add it to `Date.now()` to get the server's idea of now. The deadlines in a challenge are
 * absolute instants chosen by the server, so comparing them to an uncorrected browser
 * clock measures the disagreement between two machines as well as the time remaining: a
 * clock minutes fast offers a new code before the cooldown is up, and one minutes slow
 * shows a code as live after the server has stopped accepting it.
 *
 * The round trip is deliberately not accounted for. It is milliseconds against countdowns
 * drawn to the second, and halving an unmeasured latency would be a guess dressed as a
 * correction. An unparseable stamp yields no correction rather than `NaN`, which would
 * otherwise poison every countdown on the screen.
 */
export function clockOffset(serverTime: string | undefined, receivedAt: number): number {
  if (serverTime === undefined) {
    return 0;
  }

  const parsed = Date.parse(serverTime);
  if (Number.isNaN(parsed)) {
    return 0;
  }

  return parsed - receivedAt;
}

/** What the screen does with the answer to an ask for a code. */
export interface ResendOutcome {
  /** Which banner to raise above the field. */
  banner: 'sent' | 'already-sent';

  /** Whether what the user has already typed is still worth checking. */
  keepTypedCode: boolean;
}

/**
 * Turns the server's answer to an ask for a code into what the screen should do about it.
 *
 * The two are one decision, not two: the field is cleared exactly when a new code went
 * out, because only then is what the user typed an answer to a code that no longer exists.
 * An ask the server declined to act on leaves the live code live, so a half-typed entry is
 * still the right one and throwing it away would be the screen losing their work.
 */
export function resendOutcome(codeSent: boolean): ResendOutcome {
  return {
    banner: codeSent ? 'sent' : 'already-sent',
    keepTypedCode: !codeSent,
  };
}

/** Formats a countdown as `m:ss`, the way both the prototype and V2 show it. */
export function formatCountdown(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
